/** Response shapes returned by the ordering API. Mirrors the C# DTOs. */

export interface ProductVariant {
  id: string;
  label: string;
  sizeMl: number | null;
  price: number;
  sku: string;
  isAvailable: boolean;
}

export interface Product {
  id: string;
  name: string;
  slug: string;
  description: string;
  imageUrl: string | null;
  isAvailable: boolean;
  startingPrice: number | null;
  variants: ProductVariant[];
  /** Choices the customer must make. Empty for an ordinary drink. */
  options: ProductOptionGroup[];
}

export interface ProductOptionGroup {
  id: string;
  name: string;
  helpText: string | null;
  minSelections: number;
  maxSelections: number;
  isRequired: boolean;
  choices: ProductOptionChoice[];
}

export interface ProductOptionChoice {
  id: string;
  label: string;
  extraPrice: number;
}

/** A pick recorded on a cart line. */
export interface ChosenOption {
  groupId: string;
  groupName: string;
  choiceId: string;
  label: string;
  extraPrice: number;
}

export interface Category {
  id: string;
  name: string;
  slug: string;
  group: string;
  earnsLoyaltyStamp: boolean;
  products: Product[];
}

export interface AddOn {
  id: string;
  name: string;
  price: number;
}

export interface MenuResponse {
  categories: Category[];
  addOns: AddOn[];
}

export interface ServiceAreaSuburb {
  id: string;
  suburb: string;
  region: string;
  distanceKm: number;
}

export interface TradingStatus {
  isOpen: boolean;
  /** When the next order will actually be made. */
  opensAt: string;
  hours: string;
  message: string;
}

export interface ServiceAreaResponse {
  regions: string[];
  suburbs: ServiceAreaSuburb[];
  freeDeliveryThreshold: number;
  minimumOrderValue: number;
  directOrderDiscount: number;
  /** Google Places browser key, or null when the shop has not set one. */
  mapsApiKey: string | null;
  trading: TradingStatus;
}

export interface DeliveryQuote {
  isServiceable: boolean;
  suburb: string;
  region: string | null;
  distanceKm: number;
  uberEatsEquivalentFee: number;
  directOrderDiscount: number;
  deliveryFee: number;
  isFreeDelivery: boolean;
  subtotal: number;
  total: number;
  estimatedMinutes: number;
  unavailableReason: string | null;
}

export interface Customer {
  id: string;
  email: string;
  fullName: string;
  phone: string;
  marketingOptIn: boolean;
  notifyByEmail: boolean;
  notifyBySms: boolean;
  role: 'Customer' | 'Staff' | 'Admin' | 'SuperAdmin' | 'Driver';
  isStaff: boolean;
}

export interface AuthResponse {
  accessToken: string;
  accessTokenExpiresAt: string;
  refreshToken: string;
  refreshTokenExpiresAt: string;
  customer: Customer;
}

export interface Address {
  id: string;
  label: string;
  line1: string;
  line2: string | null;
  suburb: string;
  city: string;
  postalCode: string | null;
  instructions: string | null;
  isDefault: boolean;
}

export interface OrderItem {
  id: string;
  productName: string;
  variantLabel: string;
  imageUrl: string | null;
  unitPrice: number;
  quantity: number;
  addOns: string[];
  notes: string | null;
  lineTotal: number;
  earnsLoyaltyStamp: boolean;
}

export interface OrderDelivery {
  recipientName: string;
  phone: string;
  address: string;
  suburb: string;
  city: string;
  instructions: string | null;
  distanceKm: number;
}

export interface Order {
  id: string;
  orderNumber: string;
  status: string;
  fulfilment: string;
  subtotal: number;
  deliveryFee: number;
  loyaltyDiscount: number;
  total: number;
  notes: string | null;
  placedAt: string;
  paidAt: string | null;
  delivery: OrderDelivery | null;
  items: OrderItem[];
}

export interface PlaceOrderResponse {
  order: Order;
  paymentReference: string;
  authorizationUrl: string;
}

export interface PaymentResult {
  isPaid: boolean;
  reference: string;
  orderNumber: string;
  orderId: string;
  status: string;
  message: string | null;
}

/** Matches FulfilmentType in the API. */
export const Fulfilment = {
  Delivery: 1,
  Collection: 2,
} as const;

// ── loyalty ────────────────────────────────────────────────────────────────

export interface LoyaltyActivity {
  at: string;
  type: string;
  description: string;
  orderNumber: string | null;
}

export interface LoyaltyCard {
  enabled: boolean;
  stampCount: number;
  stampsPerReward: number;
  stampsUntilReward: number;
  availableRewards: number;
  lifetimeStamps: number;
  lifetimeRewards: number;
  lastStampAt: string | null;
  qualifyingCategories: string[];
  activity: LoyaltyActivity[];
}

export interface LoyaltyLookup {
  customerId: string;
  fullName: string;
  email: string;
  phone: string;
  stampCount: number;
  stampsPerReward: number;
  stampsUntilReward: number;
  availableRewards: number;
  lifetimeStamps: number;
  lifetimeRewards: number;
  lastStampAt: string | null;
  activity: LoyaltyActivity[];
}

export interface LoyaltyActionResult {
  success: boolean;
  message: string;
  card: LoyaltyLookup;
}

// ── staff ──────────────────────────────────────────────────────────────────

export interface StaffOrderLine {
  productName: string;
  variantLabel: string;
  quantity: number;
  addOns: string[];
  notes: string | null;
}

export interface StaffOrder {
  id: string;
  orderNumber: string;
  status: string;
  fulfilment: string;
  customerName: string;
  customerPhone: string;
  subtotal: number;
  deliveryFee: number;
  loyaltyDiscount: number;
  total: number;
  notes: string | null;
  placedAt: string;
  paidAt: string | null;
  minutesSincePaid: number;
  usedFreeItem: boolean;
  delivery: {
    recipientName: string;
    phone: string;
    address: string;
    suburb: string;
    distanceKm: number;
    instructions: string | null;
  assignedDriverId: string | null;
  assignedDriverName: string | null;
  branchId: string | null;
  branchName: string | null;
} | null;
  items: StaffOrderLine[];
  nextStatuses: string[];
  assignedDriverId: string | null;
  assignedDriverName: string | null;
}

export interface StaffBoard {
  paidCount: number;
  preparingCount: number;
  outForDeliveryCount: number;
  readyForCollectionCount: number;
  takingsToday: number;
  ordersToday: number;
  orders: StaffOrder[];
  branchId: string | null;
  branchName: string | null;
  /** False when the viewer belongs to one shop and may not look at another's board. */
  canChangeBranch: boolean;
}

export interface PaymentConfig {
  provider: 'paystack' | 'yoco';
  publicKey: string;
  currency: string;
  /** True for Yoco: the redirect proves nothing, only the webhook does. */
  confirmsByWebhookOnly: boolean;
}

// ── settings (admin) ───────────────────────────────────────────────────────

export interface SettingView {
  key: string;
  masked: string | null;
  isSecret: boolean;
  isSet: boolean;
  source: string;
  updatedAt: string | null;
}

export interface ReadinessItem {
  label: string;
  done: boolean;
  detail: string;
}

export interface SettingsResponse {
  activeProvider: string;
  paymentsReady: boolean;
  paymentsWarning: string | null;
  emailReady: boolean;
  smsReady: boolean;
  checklist: ReadinessItem[];
  webhookUrl: string | null;
  settings: SettingView[];
}

export interface RegisteredWebhook {
  id: string;
  name: string | null;
  url: string | null;
}

export interface WebhookListResult {
  success: boolean;
  message: string;
  webhooks: RegisteredWebhook[];
}

export interface WebhookRegistrationResult {
  success: boolean;
  message: string;
  detail: string | null;
  secretStored: boolean;
}

export interface ConnectionTestResult {
  success: boolean;
  message: string;
  detail: string | null;
}

export interface ForgotPasswordResponse {
  message: string;
}

export interface AdminResetResult {
  emailSent: boolean;
  message: string;
  resetLink: string;
  expiresAt: string;
}

export interface StaffMember {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  role: string;
}

// ------------------------------------------------------------------ drivers

export type VehicleType = 'Motorcycle' | 'Scooter' | 'Car' | 'Bicycle' | 'OnFoot';

export type DriverStatus = 'Draft' | 'PendingReview' | 'Approved' | 'Rejected' | 'Suspended';

export type DocumentReviewStatus = 'Pending' | 'Accepted' | 'Rejected';

export type DeliveryHandover = 'HandedToCustomer' | 'LeftWithSomeoneElse' | 'LeftAtDoor';

export type DriverDocumentType =
  | 'IdentityDocument'
  | 'DriversLicence'
  | 'ProfessionalDrivingPermit'
  | 'VehicleRegistration'
  | 'VehicleInsurance'
  | 'ProofOfAddress'
  | 'BankConfirmation'
  | 'ProfilePhoto';

export interface DriverDocumentView {
  id: string;
  type: DriverDocumentType;
  label: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  status: DocumentReviewStatus;
  expiresOn: string | null;
  isExpired: boolean;
  rejectionReason: string | null;
  uploadedAt: string;
}

export interface DriverView {
  id: string;
  customerId: string;
  fullName: string;
  email: string;
  phone: string;
  status: DriverStatus;
  vehicle: VehicleType;
  vehicleRegistration: string | null;
  isOnShift: boolean;
  canTakeDeliveries: boolean;
  completedDeliveries: number;
  statusReason: string | null;
  notes: string | null;
  submittedAt: string | null;
  reviewedAt: string | null;
  documents: DriverDocumentView[];
  required: DriverDocumentType[];
  missing: DriverDocumentType[];
  canSubmit: boolean;
}

export interface DriverJob {
  orderId: string;
  orderNumber: string;
  status: string;
  customerName: string;
  customerPhone: string;
  addressLine1: string | null;
  addressLine2: string | null;
  suburb: string | null;
  city: string | null;
  instructions: string | null;
  total: number;
  itemCount: number;
  placedAt: string;
  assignedAt: string | null;
  isDelivered: boolean;
  deliveredAt: string | null;
}

/** Labels for the vehicle picker, and which documents each obliges. */
export const VEHICLE_LABELS: Record<VehicleType, string> = {
  Motorcycle: 'Motorcycle',
  Scooter: 'Scooter',
  Car: 'Car',
  Bicycle: 'Bicycle',
  OnFoot: 'On foot',
};

export const DOCUMENT_LABELS: Record<DriverDocumentType, string> = {
  IdentityDocument: 'ID document',
  DriversLicence: "Driver's licence",
  ProfessionalDrivingPermit: 'Professional Driving Permit (PDP)',
  VehicleRegistration: 'Vehicle registration',
  VehicleInsurance: 'Vehicle insurance',
  ProofOfAddress: 'Proof of address',
  BankConfirmation: 'Bank confirmation letter',
  ProfilePhoto: 'Profile photo',
};

/** Documents that carry an expiry the shop has to chase. */
export const EXPIRING_DOCUMENTS: DriverDocumentType[] = [
  'DriversLicence',
  'ProfessionalDrivingPermit',
  'VehicleRegistration',
  'VehicleInsurance',
];

// ---------------------------------------------------------------- customers

export interface CustomerSummary {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  role: string;
  isActive: boolean;
  marketingOptIn: boolean;
  joinedAt: string;
  lastLoginAt: string | null;
  orderCount: number;
  totalSpent: number;
  lastOrderAt: string | null;
  /** Loyalty cards filled and rewarded, over their whole history. */
  cardsCompleted: number;
}

export interface CustomerPage {
  customers: CustomerSummary[];
  total: number;
  page: number;
  pageSize: number;
}

export interface CustomerOrderRow {
  id: string;
  orderNumber: string;
  status: string;
  fulfilment: string;
  total: number;
  itemCount: number;
  items: string[];
  deliveredTo: string | null;
  placedAt: string;
  completedAt: string | null;
  scheduledFor: string | null;
}

export interface CustomerDetail {
  customer: CustomerSummary;
  addresses: { id: string; label: string; address: string; isDefault: boolean }[];
  orders: CustomerOrderRow[];
  loyaltyStamps: number;
  loyaltyRewards: number;
  /** Cards completed over their whole history. */
  cardsCompleted: number;
  lifetimeStamps: number;
}

// ------------------------------------------------------------ memberships

export interface MembershipPlan {
  code: string;
  name: string;
  tagline: string;
  monthlyPrice: number;
  drinkAllowance: number;
  maxRedemptionsPerDay: number;
  memberDiscountPercent: number;
  retailValue: number;
  saving: number;
  benefits: string[];
}

export interface SubscribeResult {
  subscriptionId: string;
  membershipNumber: string;
  amountDue: number;
  /** Where to send the customer to pay. Null only if the gateway is down. */
  paymentUrl: string | null;
  paymentReference: string | null;
}

export interface Membership {
  id: string;
  membershipNumber: string;
  planCode: string;
  planName: string;
  status: string;
  isCurrent: boolean;
  pricePaid: number;
  drinkAllowance: number;
  drinksUsed: number;
  drinksRemaining: number;
  maxRedemptionsPerDay: number;
  usedToday: number;
  memberDiscountPercent: number;
  startsAt: string;
  endsAt: string;
  daysRemaining: number;
  /** Null when a drink can be taken; otherwise the reason to read out. */
  blockedReason: string | null;
  customerName: string;
  customerPhone: string;
  recent: { itemName: string; redeemedAt: string }[];
}

// -------------------------------------------------------------- menu admin

export interface MenuAdminVariant {
  id: string;
  label: string;
  sizeMl: number | null;
  price: number;
  isAvailable: boolean;
  sortOrder: number;
}

export interface MenuAdminProduct {
  id: string;
  name: string;
  isAvailable: boolean;
  variants: MenuAdminVariant[];
  description: string;
  /** Site-relative, or "api/menu/images/{id}" when uploaded in the console. */
  imageUrl: string | null;
  sortOrder: number;
}

export interface MenuAdminAddOn {
  id: string;
  name: string;
  price: number;
  isAvailable: boolean;
}

export interface MenuAdminCategory {
  id: string;
  name: string;
  isActive: boolean;
  products: MenuAdminProduct[];
  addOns: MenuAdminAddOn[];
}

/** What closing an account actually did. See StaffCustomerService.DeleteAsync. */
export interface DeleteAccountResult {
  customerId: string;
  email: string;
  /** True when the row itself went, which only happens if there were no orders. */
  removed: boolean;
  ordersRetained: number;
  documentsErased: number;
}

/** One day of a branch's week. Null times mean shut that day. */
export interface BranchDay {
  day: string;
  opens: string | null;
  closes: string | null;
}

/** A shop. */
export interface Branch {
  id: string;
  name: string;
  /** Fixed at creation, so links keep working when a shop is renamed. */
  slug: string;
  addressLine: string;
  suburb: string;
  phone: string;
  email: string;
  isActive: boolean;
  /** Where orders land when the customer did not choose. Exactly one branch holds it. */
  isDefault: boolean;
  offersCollection: boolean;
  offersDelivery: boolean;
  lastOrderMinutesBeforeClose: number;
  week: BranchDay[];
  isOpenNow: boolean;
  hoursSummary: string;
  orderCount: number;
}

export interface SaveBranchRequest {
  name: string;
  slug?: string | null;
  addressLine: string;
  suburb: string;
  phone: string;
  email: string;
  offersCollection: boolean;
  offersDelivery: boolean;
  lastOrderMinutesBeforeClose: number;
  week?: BranchDay[];
}

export type EmploymentType = 'FullTime' | 'PartTime' | 'Casual' | 'Learnership';

export type ApplicationStatus = 'New' | 'Shortlisted' | 'Interviewed' | 'Hired' | 'Declined';

/** A job the shop is advertising. */
export interface Vacancy {
  id: string;
  title: string;
  slug: string;
  employment: EmploymentType;
  summary: string;
  description: string;
  branchId: string | null;
  branchName: string | null;
  isOpen: boolean;
  closesOn: string | null;
  isAcceptingApplications: boolean;
  applicationCount: number;
}

export interface SaveVacancyRequest {
  title: string;
  employment: EmploymentType;
  summary: string;
  description: string;
  branchId?: string | null;
  closesOn?: string | null;
}

/** Somebody who applied. The CV is fetched separately and never inlined. */
export interface JobApplication {
  id: string;
  vacancyId: string | null;
  vacancyTitle: string | null;
  fullName: string;
  email: string;
  phone: string;
  coverNote: string | null;
  cvFileName: string;
  cvSizeBytes: number;
  status: ApplicationStatus;
  internalNote: string | null;
  appliedAt: string;
  reviewedAt: string | null;
}

/** Result of signing a walk-in up at the till. */
export interface CounterRegistration {
  card: LoyaltyLookup;
  /** True when the phone or email matched somebody who already had a card. */
  alreadyRegistered: boolean;
  message: string;
  /** Only when an email was given — lets them claim the account online. */
  setPasswordLink: string | null;
}

// ── corporate quotes ────────────────────────────────────────────────────────

export type QuoteStatus = 'Draft' | 'Sent' | 'Paid' | 'Fulfilled' | 'Declined' | 'Expired';

export interface QuoteLine {
  description: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
}

export interface Quote {
  id: string;
  quoteNumber: string;
  companyName: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  branchId: string | null;
  branchName: string | null;
  status: QuoteStatus;
  notes: string | null;
  discountPercent: number;
  subtotal: number;
  discountAmount: number;
  total: number;
  validUntil: string;
  hasExpired: boolean;
  createdAt: string;
  sentAt: string | null;
  paidAt: string | null;
  paymentReference: string | null;
  fulfilledAt: string | null;
  lastRemindedAt: string | null;
  reminderCount: number;
  lines: QuoteLine[];
}

export interface SaveQuoteLine {
  description: string;
  quantity: number;
  unitPrice: number;
}

export interface SaveQuoteRequest {
  companyName: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  lines: SaveQuoteLine[];
  branchId?: string | null;
  notes?: string | null;
  discountPercent?: number;
  validUntil?: string | null;
}

export interface QuoteSendResult {
  quote: Quote;
  emailSent: boolean;
  message: string;
}
